// Stripe plan configuration

export interface PlanConfig {
  id: string;
  name: string;
  nameKey: string;
  descriptionKey: string;
  monthlyPriceId: string | null;
  yearlyPriceId: string | null;
  monthlyPrice: number; // in cents (USD)
  yearlyPrice: number; // in cents (USD)
  brlMonthlyPriceId: string | null;
  brlYearlyPriceId: string | null;
  brlMonthlyPrice: number; // in centavos (BRL)
  brlYearlyPrice: number; // in centavos (BRL)
  features: string[]; // translation keys
  popular?: boolean;
  userLimit: number | 'unlimited';
}

export const PLANS: Record<string, PlanConfig> = {
  starter: {
    id: 'starter',
    name: 'Starter',
    nameKey: 'billing.plans.starter.name',
    descriptionKey: 'billing.plans.starter.description',
    monthlyPriceId: 'price_1TEdbYCmmbp0mMIH8eHCdBjz',
    yearlyPriceId: 'price_1TEdbYCmmbp0mMIHYMCWKTz9',
    monthlyPrice: 9900, // $99
    yearlyPrice: 99000, // $990
    brlMonthlyPriceId: 'price_1TF0yeCmmbp0mMIHteh3h2kh',
    brlYearlyPriceId: 'price_1TF0yvCmmbp0mMIH9GGNTP3i',
    brlMonthlyPrice: 49700, // R$497
    brlYearlyPrice: 497000, // R$4.970
    features: [
      'billing.features.users5',
      'billing.features.basicDashboards',
      'billing.features.emailSupport',
      'billing.features.crmIntegration',
    ],
    userLimit: 5,
  },
  growth: {
    id: 'growth',
    name: 'Growth',
    nameKey: 'billing.plans.growth.name',
    descriptionKey: 'billing.plans.growth.description',
    monthlyPriceId: 'price_1TEdbaCmmbp0mMIHXLX1ljDn',
    yearlyPriceId: 'price_1TEdbZCmmbp0mMIHYIHxsL3N',
    monthlyPrice: 24900, // $249
    yearlyPrice: 249000, // $2490
    brlMonthlyPriceId: 'price_1TF10bCmmbp0mMIHIFGkNkJL',
    brlYearlyPriceId: 'price_1TF10KCmmbp0mMIHEnD834tR',
    brlMonthlyPrice: 124700, // R$1.247
    brlYearlyPrice: 1247000, // R$12.470
    features: [
      'billing.features.users15',
      'billing.features.advancedAnalytics',
      'billing.features.forecast',
      'billing.features.prioritySupport',
      'billing.features.customReports',
    ],
    popular: true,
    userLimit: 15,
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    nameKey: 'billing.plans.pro.name',
    descriptionKey: 'billing.plans.pro.description',
    monthlyPriceId: 'price_1TEdbZCmmbp0mMIHihuhlc9S',
    yearlyPriceId: 'price_1TEdbZCmmbp0mMIHzrElewAV',
    monthlyPrice: 49900, // $499
    yearlyPrice: 499000, // $4990
    brlMonthlyPriceId: 'price_1TF0zRCmmbp0mMIHGULMumBk',
    brlYearlyPriceId: 'price_1TF0zjCmmbp0mMIHUQyYN5QA',
    brlMonthlyPrice: 249700, // R$2.497
    brlYearlyPrice: 2497000, // R$24.970
    features: [
      'billing.features.unlimitedUsers',
      'billing.features.aiAdvisor',
      'billing.features.playbooks',
      'billing.features.simulator',
      'billing.features.dedicatedSupport',
      'billing.features.apiAccess',
    ],
    userLimit: 'unlimited',
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    nameKey: 'billing.plans.enterprise.name',
    descriptionKey: 'billing.plans.enterprise.description',
    monthlyPriceId: null,
    yearlyPriceId: null,
    monthlyPrice: 0,
    yearlyPrice: 0,
    brlMonthlyPriceId: null,
    brlYearlyPriceId: null,
    brlMonthlyPrice: 0,
    brlYearlyPrice: 0,
    features: [
      'billing.features.unlimitedUsers',
      'billing.features.allProFeatures',
      'billing.features.sso',
      'billing.features.customIntegrations',
      'billing.features.sla',
      'billing.features.onboarding',
    ],
    userLimit: 'unlimited',
  },
};

export const getPrice = (plan: PlanConfig, interval: 'monthly' | 'yearly', currency: 'BRL' | 'USD'): number => {
  if (currency === 'BRL') {
    return interval === 'monthly' ? plan.brlMonthlyPrice : plan.brlYearlyPrice;
  }
  return interval === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
};

export const getPriceId = (plan: PlanConfig, interval: 'monthly' | 'yearly', currency: 'BRL' | 'USD'): string | null => {
  if (currency === 'BRL') {
    return interval === 'monthly' ? plan.brlMonthlyPriceId : plan.brlYearlyPriceId;
  }
  return interval === 'monthly' ? plan.monthlyPriceId : plan.yearlyPriceId;
};

export const formatPlanPrice = (cents: number, currency: 'BRL' | 'USD' = 'USD'): string => {
  if (currency === 'BRL') {
    const value = Math.round(cents / 100);
    return `R$ ${value.toLocaleString('pt-BR')}`;
  }
  const value = Math.round(cents / 100);
  return `$${value.toLocaleString('en-US')}`;
};

export const formatPrice = (cents: number, currency = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
};

export const getPlanById = (planId: string): PlanConfig | undefined => {
  return PLANS[planId];
};

export const getPlanByPriceId = (priceId: string): PlanConfig | undefined => {
  return Object.values(PLANS).find(plan => 
    plan.monthlyPriceId === priceId || plan.yearlyPriceId === priceId ||
    plan.brlMonthlyPriceId === priceId || plan.brlYearlyPriceId === priceId
  );
};
